import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import BackButton from './BackButton';
import './Auth.css';

export default function Login() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(identifier, password);
    
    setLoading(false);
    
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="login-page">
      {/* Left Side - Branding */}
      <div className="login-branding">
        <div className="login-branding-content">
          <BackButton />
          <Link to="/" className="login-logo">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="50" height="50" className="logo-img">
              <rect width="32" height="32" fill="var(--bg-tertiary)"></rect>
              <rect x="6" y="4" width="16" height="5" fill="var(--accent-primary)"></rect>
              <rect x="6" y="4" width="5" height="8" fill="var(--accent-primary)"></rect>
              <rect x="6" y="12" width="16" height="5" fill="var(--accent-primary)"></rect>
              <rect x="17" y="17" width="5" height="7" fill="var(--accent-primary)"></rect>
              <rect x="6" y="23" width="16" height="5" fill="var(--accent-primary)"></rect>
              <polygon points="22,4 26,7 26,12 22,9" fill="#1a1a2e"></polygon>
              <polygon points="22,12 26,15 26,20 22,17" fill="#1a1a2e"></polygon>
              <polygon points="22,23 26,26 26,31 22,28" fill="#1a1a2e"></polygon>
              <polygon points="6,28 22,28 26,31 10,31" fill="#1a1a2e"></polygon>
            </svg>
            <span className="logo-text">SCRAWL</span>
          </Link>
          
          <h1 className="login-headline">
            Why create an<br />
            <span className="highlight">account?</span>
          </h1>
          
          <p className="login-description">
            Unlock powerful features by signing up. It's free and takes seconds.
          </p>

          <div className="login-features">
            <div className="login-feature-item">
              <span className="login-feature-text">Access your paste history anytime</span>
            </div>
            <div className="login-feature-item">
              <span className="login-feature-text">Create private & password-protected pastes</span>
            </div>
            <div className="login-feature-item">
              <span className="login-feature-text">Syntax highlighting for 100+ languages</span>
            </div>
            <div className="login-feature-item">
              <span className="login-feature-text">Embed images in your pastes</span>
            </div>
            <div className="login-feature-item">
              <span className="login-feature-text">Zero ads, zero tracking, forever free</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="login-form-side">
        <div className="login-form-container">
          <div className="login-form-header">
            <h2>Welcome back</h2>
            <p>Sign in to continue</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            {error && (
              <div className="login-error">
                <AlertCircle size={18} />
                <span>{error}</span>
              </div>
            )}

            <div className="login-field">
              <label>Username or Email</label>
              <input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
              />
            </div>

            <div className="login-field">
              <label>Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="password-toggle"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <Link to="/forgot-password" className="forgot-password-link">
                Forgot password?
              </Link>
            </div>

            <button type="submit" disabled={loading} className="login-submit">
              {loading ? (
                'Signing in...'
              ) : (
                <>
                  Sign In
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          <div className="login-footer">
            <p>
              Don't have an account?{' '}
              <Link to="/register">Create one</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}